import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    LandPlot, Handshake, Building2, ShieldCheck, Box, Users,
    Award, Eye, HeartHandshake, Star, Calendar, Clock, Headset, ChevronRight,
} from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import PropertyCard from "../components/PropertyCard";
import PublicCockpit from "../components/PublicCockpit";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";

const ICONS = { LandPlot, Handshake, Building2, ShieldCheck, Box, Users };

const WHY_ITEMS = [
    { key: "expertise", icon: Award },
    { key: "transparency", icon: Eye },
    { key: "support", icon: HeartHandshake },
    { key: "satisfaction", icon: Star },
];

const WHY_IMG = "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

export default function Home() {
    const { t, lang } = useI18n();
    const { site } = useSite();
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/properties/featured")
            .then((r) => setFeatured(r.data.slice(0, 4)))
            .catch(() => setFeatured([]));
    }, []);

    const hero = site?.hero || {};
    const services = site?.services || [];
    const hours = site?.hours || {};
    const cta = site?.cta_band || {};
    const clientsCount = site?.clients_satisfied ?? 500;

    const heroImg = hero.image_url || "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1920";
    const heroKicker = lang === "en" ? hero.kicker_en : hero.kicker_fr;
    const heroTitleA = lang === "en" ? hero.title_a_en : hero.title_a_fr;
    const heroTitleB = lang === "en" ? hero.title_b_en : hero.title_b_fr;
    const heroDesc = lang === "en" ? hero.description_en : hero.description_fr;
    const ctaText = lang === "en" ? cta.text_en : cta.text_fr;
    const ctaButton = lang === "en" ? cta.button_en : cta.button_fr;

    return (
        <PublicLayout>
            {/* HERO */}
            <section className="relative bg-brand-navy text-white overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={heroImg} alt="Villa moderne" className="w-full h-full object-cover opacity-70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/20" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
                        {heroKicker ? (
                            <div className="inline-flex items-center gap-3 text-brand-gold text-[0.7rem] tracking-[0.32em] uppercase font-semibold mb-6">
                                <span className="w-10 h-px bg-brand-gold" />
                                {heroKicker}
                            </div>
                        ) : null}
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-semibold">
                            {heroTitleA || t("hero.title_a")}
                            <br />
                            <span className="italic text-brand-gold">{heroTitleB || t("hero.title_b")}</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
                            {heroDesc || t("hero.description")}
                        </p>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <Link to="/biens" data-testid="hero-cta-primary" className="inline-flex items-center px-8 py-4 bg-brand-emerald hover:bg-brand-emerald-soft text-white text-[0.72rem] tracking-[0.24em] uppercase font-semibold transition-colors">
                                {t("hero.cta_primary")} <ChevronRight className="w-4 h-4 ml-2" />
                            </Link>
                            <Link to="/contact" data-testid="hero-cta-secondary" className="inline-flex items-center px-8 py-4 btn-ghost-white text-[0.72rem] tracking-[0.24em] uppercase font-semibold hover:bg-white/20 transition-colors">
                                {t("hero.cta_secondary")}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* PUBLIC COCKPIT */}
            <PublicCockpit />

            {/* SERVICES */}
            <section className="py-20 sm:py-28 bg-brand-paper" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
                            01 — {t("services.title")}
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl text-brand-navy">{t("services.title")}</h2>
                        <div className="w-12 h-px bg-brand-gold mx-auto my-5" />
                        <p className="text-gray-600">{t("services.subtitle")}</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((s, idx) => {
                            const Icon = ICONS[s.icon] || Users;
                            const title = lang === "en" ? s.title_en || s.title_fr : s.title_fr;
                            const text = lang === "en" ? s.text_en || s.text_fr : s.text_fr;
                            return (
                                <motion.div
                                    key={s.key || idx}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.5, delay: idx * 0.05 }}
                                    className="group bg-white p-8 border border-gray-100 hover:border-brand-gold/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all"
                                    data-testid={`service-${s.key}`}
                                >
                                    <div className="w-14 h-14 bg-brand-emerald/10 inline-flex items-center justify-center mb-5 group-hover:bg-brand-emerald transition-colors">
                                        <Icon className="w-6 h-6 text-brand-emerald group-hover:text-white transition-colors" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-display text-xl text-brand-navy mb-2">{title}</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FEATURED PROPERTIES */}
            <section className="py-20 sm:py-28 bg-white" data-testid="featured-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-6">
                        <div>
                            <div className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
                                02 — {t("featured.title")}
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl text-brand-navy">{t("featured.title")}</h2>
                            <div className="w-12 h-px bg-brand-gold mt-4" />
                        </div>
                        <Link to="/biens" className="inline-flex items-center text-[0.72rem] tracking-[0.22em] uppercase font-semibold text-brand-navy hover:text-brand-gold transition-colors" data-testid="see-all-properties">
                            {t("featured.see_all")} <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {featured.map((p) => <PropertyCard key={p.id} p={p} />)}
                    </div>
                </div>
            </section>

            {/* WHY + HOURS */}
            <section className="py-20 sm:py-28 bg-brand-paper" data-testid="why-hours-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5 bg-brand-navy text-white p-10">
                        <div className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
                            03 — {t("why.title")}
                        </div>
                        <h3 className="font-display text-3xl text-white mb-8">{t("why.title")}</h3>
                        <ul className="space-y-6">
                            {WHY_ITEMS.map(({ key, icon: Icon }) => (
                                <li key={key} className="flex gap-4">
                                    <div className="w-10 h-10 bg-brand-gold/15 flex items-center justify-center flex-shrink-0">
                                        <Icon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <div className="text-[0.78rem] tracking-[0.18em] uppercase font-semibold text-brand-gold">
                                            {t(`why.items.${key}.title`)}
                                        </div>
                                        <p className="text-sm text-white/80 mt-1 leading-relaxed">
                                            {t(`why.items.${key}.text`)}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-4 relative overflow-hidden min-h-[260px]">
                        <img src={WHY_IMG} alt="Intérieur" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent" />
                        <div className="relative h-full p-8 flex items-end">
                            <div className="text-white">
                                <div className="font-display text-5xl text-brand-gold">+{clientsCount}</div>
                                <div className="text-xs tracking-[0.22em] uppercase font-semibold mt-1">
                                    {t("why.satisfied")}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-3 bg-white border border-gray-100 p-8 flex flex-col">
                        <div className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-brand-gold mb-3">
                            04 — {t("hours.title")}
                        </div>
                        <h3 className="font-display text-2xl text-brand-navy mb-6">{t("hours.title")}</h3>
                        <div className="space-y-3 text-sm text-gray-700 flex-1">
                            <HRow label={t("hours.mon_thu")} value={hours.mon_thu} />
                            <HRow label={t("hours.fri")} value={hours.fri} />
                            <HRow label={t("hours.sat")} value={hours.sat} />
                            <HRow label={t("hours.sun")} value={hours.sun} icon={Clock} />
                        </div>
                        <div className="mt-6 bg-brand-gold text-white px-4 py-3 flex items-center gap-3">
                            <Headset className="w-5 h-5" />
                            <div className="text-[0.7rem] tracking-[0.18em] uppercase font-semibold leading-tight">
                                {t("hours.ribbon")}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA BAND */}
            <section className="bg-brand-gold text-brand-navy" data-testid="cta-band">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
                    <p className="font-display text-2xl sm:text-3xl">{ctaText || t("cta_band.text")}</p>
                    <Link to="/contact" className="inline-flex items-center px-8 py-4 bg-brand-navy text-white hover:bg-white hover:text-brand-navy text-[0.72rem] tracking-[0.24em] uppercase font-semibold transition-colors" data-testid="cta-band-button">
                        {ctaButton || t("cta_band.button")} <ChevronRight className="w-4 h-4 ml-2" />
                    </Link>
                </div>
            </section>
        </PublicLayout>
    );
}

function HRow({ label, value, icon: Icon = Calendar }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-brand-emerald" />{label}</span>
            <span className="font-semibold text-right whitespace-pre-line">{value || "—"}</span>
        </div>
    );
}
